import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@vibrent/electryon';
import { createKeycloakAdapter } from '../../utils/keycloakAdapter';
import {
  getAuthenticatedKeycloakTokens,
  getKeycloakSsoInFlight,
  normalizeKeycloakConfig,
  setKeycloakSsoInFlight,
  shouldSkipUserInfoSync,
} from '../../utils/authFlow';
import {
  checkLogin,
  getKeyCloakURL,
  getUserInfoDetails,
  skipSessionProbe,
  shouldSkipSessionProbe,
} from '../login/actions';
import applyUserLocalePreferences from '../../utils/applyUserLocalePreferences';
import * as spinnerStyle from '../logout/style.scss';

const getProgramCodeFromLocation = () => {
  const decodedURL = decodeURIComponent(window.location.search);
  const newURL = decodedURL.replace(/\?/g, '&');
  return new URLSearchParams(newURL).get('program');
};

const restoreSessionFromKeycloak = (keyCloakConfig, actions) => {
  if (getKeycloakSsoInFlight()) {
    return getKeycloakSsoInFlight();
  }

  const request = (async () => {
    try {
      const program = getProgramCodeFromLocation();
      const existingTokens = getAuthenticatedKeycloakTokens();
      if (existingTokens) {
        if (!shouldSkipUserInfoSync(existingTokens.accessToken)) {
          await actions.getUserInfoDetails(
            existingTokens.accessToken,
            existingTokens.idToken,
            existingTokens.refreshToken,
            program,
          );
        }
        return;
      }

      const adapter = createKeycloakAdapter(keyCloakConfig);
      const authenticated = await adapter.init({
        onLoad: 'check-sso',
        flow: 'standard',
      });

      if (authenticated && adapter.isAuthenticated()) {
        window.keycloakAuth = adapter.getInstance && adapter.getInstance();
        await actions.getUserInfoDetails(
          adapter.getToken(),
          adapter.getIdToken(),
          adapter.getRefreshToken(),
          program,
        );
        return;
      }

      await actions.checkLogin({ restoreSession: true });
    } catch (e) {
      try {
        await actions.checkLogin({ restoreSession: true });
      } catch (loginError) {
        actions.skipSessionProbe();
      }
    } finally {
      setKeycloakSsoInFlight(null);
    }
  })();

  setKeycloakSsoInFlight(request);
  return request;
};

/**
 * Decides whether a full startup session probe needs to run for the current
 * mount. We only probe on protected routes when there is no session yet.
 */
const shouldRunStartupBootstrap = props => (
  !shouldSkipSessionProbe() && !props.isLoggedIn && !props.userDetail
);

/**
 * Restores Keycloak SSO on app load (protected routes only).
 */
const SessionBootstrap = (props) => {
  const bootstrapStarted = useRef(false);
  const [bootstrapComplete, setBootstrapComplete] = useState(
    () => !shouldRunStartupBootstrap(props),
  );
  const { i18n } = useTranslation();
  const loadedUserKeyRef = useRef(null);

  useEffect(() => {
    const { userDetail } = props;
    if (!props.isLoggedIn || !userDetail) {
      return;
    }
    const userKey = userDetail.username || userDetail.id;
    if (loadedUserKeyRef.current === userKey) {
      return;
    }
    loadedUserKeyRef.current = userKey;
    applyUserLocalePreferences(userDetail, i18n);
  }, [props.userDetail, props.isLoggedIn]);

  useEffect(() => {
    if (bootstrapStarted.current) {
      return;
    }
    bootstrapStarted.current = true;

    const bootstrap = async () => {
      if (shouldSkipSessionProbe()) {
        props.actions.skipSessionProbe();
        return;
      }

      if (props.isLoggedIn || props.userDetail) {
        props.actions.skipSessionProbe();
        return;
      }

      let config = normalizeKeycloakConfig(props.keyCloakConfig);
      if (!config) {
        const fetched = await props.actions.getKeyCloakURL();
        config = normalizeKeycloakConfig(fetched);
      }

      if (!config) {
        props.actions.skipSessionProbe();
        return;
      }

      await restoreSessionFromKeycloak(config, props.actions);
    };

    bootstrap()
      .catch(() => props.actions.skipSessionProbe())
      .finally(() => setBootstrapComplete(true));
  }, []);

  if (!bootstrapComplete) {
    return (
      <div className={spinnerStyle['log-out-spinner-container']}>
        <Spinner />
      </div>
    );
  }

  return props.children;
};

const mapStateToProps = state => ({
  userDetail: state.UserDetail.get('userDetail'),
  isLoggedIn: state.UserDetail.get('isLoggedIn'),
  keyCloakConfig: state.LoginReducer.get('keyCloakConfig'),
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    checkLogin,
    getKeyCloakURL,
    getUserInfoDetails,
    skipSessionProbe,
  }, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SessionBootstrap);

export { SessionBootstrap as SessionBootstrapComponent, restoreSessionFromKeycloak };

SessionBootstrap.propTypes = {
  children: PropTypes.node.isRequired,
  userDetail: PropTypes.shape({
    username: PropTypes.string,
    id: PropTypes.string,
  }),
  isLoggedIn: PropTypes.bool,
  keyCloakConfig: PropTypes.shape({}),
  actions: PropTypes.shape({
    checkLogin: PropTypes.func.isRequired,
    getKeyCloakURL: PropTypes.func.isRequired,
    getUserInfoDetails: PropTypes.func.isRequired,
    skipSessionProbe: PropTypes.func.isRequired,
  }).isRequired,
};

SessionBootstrap.defaultProps = {
  userDetail: undefined,
  isLoggedIn: false,
  keyCloakConfig: null,
};
